
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Settings, 
  Save, 
  ArrowLeft, 
  Globe, 
  Users, 
  Shield, 
  Bell,
  Database,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface PlatformSettingsProps {
  onBack: () => void;
}

interface PlatformConfig {
  site_name: string;
  site_description: string;
  maintenance_mode: boolean;
  registration_enabled: boolean;
  email_verification_required: boolean;
  max_users_per_team: number;
  default_user_role: string;
  notification_settings: {
    email_enabled: boolean;
    push_enabled: boolean;
    sms_enabled: boolean;
  };
  security_settings: {
    password_min_length: number;
    require_2fa: boolean;
    session_timeout_minutes: number;
    max_login_attempts: number;
  };
  content_settings: {
    allow_user_content: boolean;
    moderate_content: boolean;
    max_file_size_mb: number;
  };
}

const PlatformSettings: React.FC<PlatformSettingsProps> = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<PlatformConfig>({
    site_name: 'Players Locker Room',
    site_description: 'The ultimate sports community platform',
    maintenance_mode: false,
    registration_enabled: true,
    email_verification_required: true,
    max_users_per_team: 25,
    default_user_role: 'player',
    notification_settings: {
      email_enabled: true,
      push_enabled: true,
      sms_enabled: false
    },
    security_settings: {
      password_min_length: 8,
      require_2fa: false,
      session_timeout_minutes: 1440, // 24 hours
      max_login_attempts: 5
    },
    content_settings: {
      allow_user_content: true,
      moderate_content: true,
      max_file_size_mb: 10
    }
  });

  useEffect(() => {
    loadPlatformSettings();
  }, []);

  const loadPlatformSettings = async () => {
    try {
      setLoading(true);
      // In a real implementation, you'd load these from a platform_settings table
      // For now, we'll use the default config
      console.log('Platform settings loaded');
    } catch (error) {
      console.error('Error loading platform settings:', error);
      toast.error('Failed to load platform settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      // In a real implementation, you'd save these to a database table
      console.log('Saving platform settings:', config);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Platform settings saved successfully');
    } catch (error) {
      console.error('Error saving platform settings:', error);
      toast.error('Failed to save platform settings');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (path: string, value: any) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      const keys = path.split('.');
      let current: any = newConfig;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Loading platform settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          onClick={onBack}
          variant="outline"
          className="border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Panel
        </Button>
        <h1 className="text-2xl font-bold text-white">Platform Settings</h1>
      </div>

      {/* General Settings */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-400" />
            General Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="site-name" className="text-gray-300">Site Name</Label>
              <Input
                id="site-name"
                value={config.site_name}
                onChange={(e) => updateConfig('site_name', e.target.value)}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label htmlFor="default-role" className="text-gray-300">Default User Role</Label>
              <Select
                value={config.default_user_role}
                onValueChange={(value) => updateConfig('default_user_role', value)}
              >
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  <SelectItem value="player">Player</SelectItem>
                  <SelectItem value="fan">Fan</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label htmlFor="site-description" className="text-gray-300">Site Description</Label>
            <Textarea
              id="site-description"
              value={config.site_description}
              onChange={(e) => updateConfig('site_description', e.target.value)}
              className="bg-gray-700 border-gray-600 text-white"
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="maintenance-mode" className="text-gray-300">Maintenance Mode</Label>
              <Switch
                id="maintenance-mode"
                checked={config.maintenance_mode}
                onCheckedChange={(checked) => updateConfig('maintenance_mode', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="registration-enabled" className="text-gray-300">Registration Enabled</Label>
              <Switch
                id="registration-enabled"
                checked={config.registration_enabled}
                onCheckedChange={(checked) => updateConfig('registration_enabled', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Management */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="h-5 w-5 text-green-400" />
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="max-users-per-team" className="text-gray-300">Max Users Per Team</Label>
              <Input
                id="max-users-per-team"
                type="number"
                value={config.max_users_per_team}
                onChange={(e) => updateConfig('max_users_per_team', parseInt(e.target.value) || 25)}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div className="flex items-center justify-between pt-6">
              <Label htmlFor="email-verification" className="text-gray-300">Require Email Verification</Label>
              <Switch
                id="email-verification"
                checked={config.email_verification_required}
                onCheckedChange={(checked) => updateConfig('email_verification_required', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="h-5 w-5 text-red-400" />
            Security Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="password-min-length" className="text-gray-300">Minimum Password Length</Label>
              <Input
                id="password-min-length"
                type="number"
                value={config.security_settings.password_min_length}
                onChange={(e) => updateConfig('security_settings.password_min_length', parseInt(e.target.value) || 8)}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label htmlFor="max-login-attempts" className="text-gray-300">Max Login Attempts</Label>
              <Input
                id="max-login-attempts"
                type="number"
                value={config.security_settings.max_login_attempts}
                onChange={(e) => updateConfig('security_settings.max_login_attempts', parseInt(e.target.value) || 5)}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="session-timeout" className="text-gray-300">Session Timeout (minutes)</Label>
            <Input
              id="session-timeout"
              type="number"
              value={config.security_settings.session_timeout_minutes}
              onChange={(e) => updateConfig('security_settings.session_timeout_minutes', parseInt(e.target.value) || 1440)}
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="require-2fa" className="text-gray-300">Require Two-Factor Authentication</Label>
            <Switch
              id="require-2fa"
              checked={config.security_settings.require_2fa}
              onCheckedChange={(checked) => updateConfig('security_settings.require_2fa', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Bell className="h-5 w-5 text-yellow-400" />
            Notification Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="email-notifications" className="text-gray-300">Email Notifications</Label>
              <Switch
                id="email-notifications"
                checked={config.notification_settings.email_enabled}
                onCheckedChange={(checked) => updateConfig('notification_settings.email_enabled', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="push-notifications" className="text-gray-300">Push Notifications</Label>
              <Switch
                id="push-notifications"
                checked={config.notification_settings.push_enabled}
                onCheckedChange={(checked) => updateConfig('notification_settings.push_enabled', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="sms-notifications" className="text-gray-300">SMS Notifications</Label>
              <Switch
                id="sms-notifications"
                checked={config.notification_settings.sms_enabled}
                onCheckedChange={(checked) => updateConfig('notification_settings.sms_enabled', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Settings */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Database className="h-5 w-5 text-purple-400" />
            Content Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="max-file-size" className="text-gray-300">Max File Size (MB)</Label>
              <Input
                id="max-file-size"
                type="number"
                value={config.content_settings.max_file_size_mb}
                onChange={(e) => updateConfig('content_settings.max_file_size_mb', parseInt(e.target.value) || 10)}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="allow-user-content" className="text-gray-300">Allow User Content</Label>
                <Switch
                  id="allow-user-content"
                  checked={config.content_settings.allow_user_content}
                  onCheckedChange={(checked) => updateConfig('content_settings.allow_user_content', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="moderate-content" className="text-gray-300">Moderate Content</Label>
                <Switch
                  id="moderate-content"
                  checked={config.content_settings.moderate_content}
                  onCheckedChange={(checked) => updateConfig('content_settings.moderate_content', checked)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSaveSettings}
          disabled={saving}
          className="bg-sports-green text-black hover:bg-green-600"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default PlatformSettings;
